/**
 * AppShell — the shared chrome every App renders inside.
 *
 * Deliberately minimal: one sticky header (suite link, app identity, accent),
 * one centered content column. No sidebar, no B-Mode navigation. Works from
 * phone to desktop. Product surface, light + dark via shared tokens.
 */

import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { APP_ACCENT_CLASSES, type AppDefinition } from '../registry';

export function AppShell({
  app,
  backTo,
  backLabel,
  actions,
  children,
  wide = false,
}: {
  app: AppDefinition;
  /** Where the header back-arrow points. Defaults to the suite launcher. */
  backTo?: string;
  backLabel?: string;
  /** Optional right-aligned header actions (kept to one or two, C Mode). */
  actions?: ReactNode;
  children: ReactNode;
  /** Opt into a wider column for tabular screens (e.g. launch calendar). */
  wide?: boolean;
}) {
  const accent = APP_ACCENT_CLASSES[app.accent];
  return (
    <div className="min-h-screen flex flex-col bg-gray-50 dark:bg-[var(--bg)]">
      {/* pt-[env(...)] keeps the header clear of the notch/status bar when the
          app runs standalone (installed PWA) or inside the native shells. */}
      <header className="sticky top-0 z-40 border-b border-gray-200 bg-white pt-[env(safe-area-inset-top)] dark:bg-[var(--surface)] dark:border-[var(--rule)]">
        <div className={`mx-auto flex h-14 items-center gap-3 px-4 ${wide ? 'max-w-6xl' : 'max-w-3xl'}`}>
          <Link
            to={backTo ?? '/apps'}
            aria-label={backLabel ?? 'Back to apps'}
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md text-gray-500 hover:bg-gray-100 dark:text-[var(--text-sec)] dark:hover:bg-[var(--hover-tint)]"
          >
            <ArrowLeft className="h-4 w-4" />
          </Link>
          <span
            className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg font-heading font-semibold text-sm ${accent.chipBg} ${accent.chipText}`}
          >
            {app.name.charAt(0)}
          </span>
          <div className="min-w-0 flex-1">
            <h1 className="truncate font-heading font-semibold text-gray-900 dark:text-[var(--text-pri)]">
              {app.name}
            </h1>
          </div>
          {actions}
          <span className="hidden sm:block font-mono text-[10px] uppercase tracking-[0.18em] text-gray-400 dark:text-[var(--text-ter)]">
            Priorities.ai
          </span>
        </div>
        <div className={`h-0.5 ${accent.bar}`} />
      </header>
      <main
        className={`mx-auto w-full flex-1 px-4 py-6 pb-[calc(1.5rem+env(safe-area-inset-bottom))] sm:py-8 ${wide ? 'max-w-6xl' : 'max-w-3xl'}`}
      >
        {children}
      </main>
    </div>
  );
}

/** C Mode rule 4: every screen names where the ball is. */
export function BallLine({ ballWith }: { ballWith: string }) {
  return (
    <p className="mb-4 font-mono text-[11px] uppercase tracking-[0.16em] text-gray-500 dark:text-[var(--text-sec)]">
      The ball is with <span className="text-gray-900 dark:text-[var(--text-pri)]">{ballWith}</span>
    </p>
  );
}

/** Screen title + optional one-sentence subtitle, consistent across apps. */
export function ScreenHeading({ title, subtitle }: { title: string; subtitle?: string }) {
  return (
    <div className="mb-5">
      <h2 className="font-heading font-semibold text-xl text-gray-900 dark:text-[var(--text-pri)]">
        {title}
      </h2>
      {subtitle ? (
        <p className="mt-1 font-body text-sm text-gray-600 dark:text-[var(--text-sec)]">{subtitle}</p>
      ) : null}
    </div>
  );
}
